import React, { useEffect, useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faUser,
    faXmark,
    faCopy,
    faCircleCheck,
    faLock,
    faPenNib,
    faMusic,
    faCubes,
    faLightbulb,
} from '@fortawesome/free-solid-svg-icons';
import { entityRef, revokeViewer } from '../shareTarget';
import User from '../models/User';
import { relativeTime } from '../utils';

const TARGET_ICONS = {
    project: faCubes,
    track: faMusic,
    lyrics: faPenNib,
    idea: faLightbulb,
};

const formatViewerName = (profile) => {
    if (!profile) return null;
    const first = (profile.firstName || '').trim();
    const last = (profile.lastName || '').trim();
    if (first && last) return `${first} ${last}`;
    return first || last || null;
};

// Mirrors iOS ManageAccessVC. Two sections:
//   1. People with access — durable grants from /grantedViewers
//   2. Active invite links — tokens from listProjectShareLinks
// Each row swipes/clicks to revoke through the appropriate CF.
const ManageAccess = ({ target, accessMode, onBack, onClose }) => {
    const isPublic = accessMode === 'public';
    const targetNoun = target?.kind === 'track' ? 'track' : 'project';
    const [grants, setGrants] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [profiles, setProfiles] = useState({});
    const [error, setError] = useState('');
    const [copiedToken, setCopiedToken] = useState(null);

    // Read /grantedViewers directly. RTDB rules allow members; the redeem CF
    // is the only writer.
    const refreshGrants = async () => {
        try {
            const snap = await entityRef(target).child('grantedViewers').once('value');
            const raw = snap.val() || {};
            const list = Object.entries(raw).map(([uid, val]) => ({
                uid,
                permission: val?.permission || 'view',
                lastVia: val?.['last via'] || null,
                lastSourceId: val?.['last source id'] || null,
                grantedAt: val?.['granted at'] || null,
                lastRedeemedAt: val?.['last redeemed at'] || null,
            }));
            list.sort((a, b) => (b.lastRedeemedAt || 0) - (a.lastRedeemedAt || 0));
            setGrants(list);
        } catch (err) {
            setError(err?.message || "Couldn't load access list.");
            setGrants([]);
        }
    };

    const refreshTokens = async () => {
        try {
            const fn = firebase.functions().httpsCallable('listProjectShareLinks');
            const res = await fn({
                artistId: target.artistId,
                projectId: target.projectId,
            });
            const all = res?.data?.links || [];
            // listProjectShareLinks returns project + per-track tokens together.
            // Filter to current target. CF response surfaces `track id` so
            // track-target rows can match exactly; project rows match by type.
            const filtered = all.filter((link) => {
                if (target.kind === 'track') {
                    return link['target type'] === 'track' &&
                        (!link['track id'] || link['track id'] === target.trackId);
                }
                return link['target type'] === 'project';
            });
            setTokens(filtered);
        } catch (err) {
            setError(err?.message || "Couldn't load invite links.");
            setTokens([]);
        }
    };

    useEffect(() => {
        refreshGrants();
        refreshTokens();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target?.kind, target?.artistId, target?.projectId, target?.trackId]);

    // Resolve viewer uids to display names via /users/{uid}/publicProfile,
    // the cross-user-readable subtree. The locked /users/{uid}/metadata
    // stays inaccessible to non-self callers, so foreign-uid lookups must
    // go through publicProfile (seeded by signup + User.setName fan-outs).
    useEffect(() => {
        if (!grants || grants.length === 0) return;
        let cancelled = false;
        grants.forEach((g) => {
            if (!g.uid || profiles[g.uid] !== undefined) return;
            User.getPublicProfile(g.uid).then((p) => {
                if (cancelled) return;
                setProfiles((prev) => ({ ...prev, [g.uid]: p || null }));
            }).catch((err) => {
                if (cancelled) return;
                console.warn('Could not resolve viewer name', g.uid, err?.code || err?.message);
                setProfiles((prev) => ({ ...prev, [g.uid]: null }));
            });
        });
        return () => { cancelled = true; };
    }, [grants]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRevokeViewer = async (g) => {
        const name = formatViewerName(profiles[g.uid]) || 'this viewer';
        if (!window.confirm(`Remove ${name}? They'll lose access immediately.`)) return;
        try {
            await revokeViewer(target, g.uid);
            await refreshGrants();
        } catch (err) {
            setError(err?.message || "Couldn't remove access.");
        }
    };

    const handleRevokeToken = async (token) => {
        if (!window.confirm("Revoke this link? Anyone holding it can no longer redeem. People who already redeemed keep access.")) return;
        try {
            const fn = firebase.functions().httpsCallable('revokeShareLink');
            await fn({ token });
            await refreshTokens();
        } catch (err) {
            setError(err?.message || "Couldn't revoke link.");
        }
    };

    const handleCopyToken = async (token, url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedToken(token);
            setTimeout(() => setCopiedToken((cur) => (cur === token ? null : cur)), 1600);
        } catch (_e) { /* clipboard blocked */ }
    };

    const activeTokens = (tokens || []).filter((link) => {
        if (link.revoked) return false;
        if (link['expires at'] && link['expires at'] < Date.now()) return false;
        if (link['max uses'] && (link['click count'] || 0) >= link['max uses']) return false;
        return true;
    });

    const isEmpty = (grants?.length === 0) && (activeTokens.length === 0);

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>MANAGE LINKS & ACCESS</span>
                </div>
                <div className="modal-title">{target?.name || 'Untitled'}</div>
                {target?.subtitle && (
                    <div className="modal-subtitle">{target.subtitle}</div>
                )}

                {error && <div className="new-project-error">{error}</div>}

                {grants === null || tokens === null ? (
                    <div className="share-empty">Loading…</div>
                ) : isEmpty ? (
                    <div className="share-empty">No one has access yet.</div>
                ) : (
                    <>
                        {grants.length > 0 && (
                            <>
                                <div className="share-section-title">PEOPLE WITH ACCESS</div>
                                {isPublic && (
                                    <div className="share-public-notice">
                                        This {targetNoun} is public — anyone with the link can view. Switch to Private to control individual access.
                                    </div>
                                )}
                                <div className="share-link-list">
                                    {grants.map((g) => {
                                        const name = formatViewerName(profiles[g.uid]);
                                        const last = g.lastRedeemedAt ? relativeTime(g.lastRedeemedAt) : '';
                                        return (
                                            <div key={g.uid} className="share-link-row">
                                                <span className="share-link-row-icon icon-blue">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </span>
                                                <div className="share-link-row-main">
                                                    <div className="share-link-row-title">
                                                        {name ? (
                                                            name
                                                        ) : profiles[g.uid] === undefined ? (
                                                            <span className="skeleton-line viewer-name-skeleton" />
                                                        ) : (
                                                            <span className="viewer-name-unknown">Viewer {String(g.uid).slice(0, 6)}</span>
                                                        )}
                                                        <span className={`share-permission-chip ${g.permission === 'editor' ? 'is-editor' : ''}`}>
                                                            {g.permission === 'editor' ? <FontAwesomeIcon icon={faPenNib} /> : null}
                                                            {g.permission === 'editor' ? 'editor' : 'view'}
                                                        </span>
                                                    </div>
                                                    <div className="share-link-row-sub">
                                                        {g.lastVia ? `via ${g.lastVia}` : 'via link'}
                                                        {last ? ` · last ${last} ago` : ''}
                                                    </div>
                                                </div>
                                                <div className="share-link-row-actions">
                                                    <button
                                                        className="btn-ghost"
                                                        onClick={() => handleRevokeViewer(g)}
                                                        disabled={isPublic}
                                                        title={isPublic ? `Switch this ${targetNoun} to Private to remove individual viewers.` : ''}
                                                    >
                                                        <FontAwesomeIcon icon={faXmark} /> Remove
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {activeTokens.length > 0 && (
                            <>
                                <div className="share-section-title">ACTIVE INVITE LINKS</div>
                                <div className="share-link-list">
                                    {activeTokens.map((link) => {
                                        const expired = link['expires at'] && link['expires at'] < Date.now();
                                        const usedUp = link['max uses'] && (link['click count'] || 0) >= link['max uses'];
                                        const status = link.revoked ? 'revoked'
                                            : expired ? 'expired'
                                            : usedUp ? 'expired'
                                            : 'active';
                                        const statusLabel = link.revoked ? 'Revoked'
                                            : expired ? 'Expired'
                                            : usedUp ? 'Used up'
                                            : 'Active';
                                        const isCopied = copiedToken === link.token;
                                        const isActive = status === 'active';
                                        return (
                                            <div key={link.token} className="share-link-row">
                                                <span className="share-link-row-icon icon-purple">
                                                    <FontAwesomeIcon icon={TARGET_ICONS[link['target type']] || faUsers} />
                                                </span>
                                                <div className="share-link-row-main">
                                                    <div className="share-link-row-title">
                                                        <span className="share-link-row-slug">{link.token}</span>
                                                        <span className={`share-permission-chip ${link.permission === 'editor' ? 'is-editor' : ''}`}>
                                                            {link.permission === 'editor' ? <FontAwesomeIcon icon={faPenNib} /> : null}
                                                            {link.permission}
                                                        </span>
                                                        {link.hasPassword && (
                                                            <span className="share-permission-chip">
                                                                <FontAwesomeIcon icon={faLock} /> protected
                                                            </span>
                                                        )}
                                                        <span className={`share-status-chip chip-${status}`}>
                                                            {statusLabel}
                                                        </span>
                                                    </div>
                                                    <div className="share-link-row-sub">
                                                        {link['click count'] || 0} use{(link['click count'] || 0) === 1 ? '' : 's'}
                                                        {link['max uses'] ? ` / ${link['max uses']}` : ''}
                                                        {link['expires at']
                                                            ? ` · ${expired ? 'expired' : `expires in ${relativeTime(Date.now() - (link['expires at'] - Date.now()))}`}`
                                                            : ''}
                                                    </div>
                                                    <code className="share-link-row-url">{link.url}</code>
                                                </div>
                                                <div className="share-link-row-actions">
                                                    {isActive && (
                                                        <button
                                                            className={`btn-ghost ${isCopied ? 'is-copied' : ''}`}
                                                            onClick={() => handleCopyToken(link.token, link.url)}
                                                        >
                                                            <FontAwesomeIcon icon={isCopied ? faCircleCheck : faCopy} />
                                                            {isCopied ? ' Copied' : ' Copy'}
                                                        </button>
                                                    )}
                                                    {!link.revoked && (
                                                        <button className="btn-ghost" onClick={() => handleRevokeToken(link.token)}>
                                                            <FontAwesomeIcon icon={faXmark} /> Revoke
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}

                <div className="new-project-actions">
                    <button className="btn-ghost" onClick={onBack}>Back</button>
                    <button className="btn-primary" onClick={onClose}>Done</button>
                </div>
            </div>
        </div>
    );
};

export default ManageAccess;
